import { CELL, GROUND } from '../core/config';
import type { City, Place } from '../world/city';
import type { PhysicsCity } from '../physics/world';
import type { Vec } from '../destruction/structure';
import { roadHeight, roadLength, roadPoint, roadSurfaceHeight } from '../world/roads';
import { MinQueue } from '../core/priority';
import { inRiver, TrafficSurface } from '../world/surface-access';
import { militaryZone } from '../world/strategic-districts';
import { railStations } from '../world/rail';
export interface WalkNode extends Vec {
  restricted?: boolean;
  id: number;
  layer: string;
  supportCell?: number;
  buildingId?: number;
}
export interface WalkLink {
  id: number;
  a: number;
  b: number;
  kind: 'walk' | 'crossing' | 'door' | 'lift';
  resource?: string;
  junction?: number;
  axis?: number;
  buildingId?: number;
  /** When present, normal travel is a -> b. Reverse searches follow its reverse. */
  oneWay?: boolean;
  /** Authored node positions are fixed; closures change availability, not length. */
  distance?: number;
}
interface WalkCorridor {
  nodes: number[];
  links: WalkLink[];
}
export class WalkingNetwork {
  nodes: WalkNode[] = [];
  links: WalkLink[] = [];
  adjacent = new Map<number, WalkLink[]>();
  places = new Map<string, number>();
  private state = new Map<number, { at: number; open: boolean }>();
  private cacheWrites = 0;
  private invalidated = new Map<number, { writes: number; x: number; z: number; radius: number }>();
  private bins = new Map<string, WalkNode[]>();
  private linkBins = new Map<string, Set<number>>();
  private availability = new WeakMap<
    Place,
    {
      version: number;
      support?: number;
      furniture?: number;
      supportVersion: number;
      furnitureVersion: number;
      value: boolean;
    }
  >();
  private authoredPlaces = new Map<string, number>();
  private corridors = new Map<number, WalkCorridor[]>();
  private corridorNodeCount = 0;
  private corridorLinkCount = 0;
  private portalLinks = 0;
  private layerPortals = new Map<string, Set<number>>();
  searchCount = 0;
  searchVisits = 0;
  searchLinkChecks = 0;
  compressedSearchSteps = 0;
  prunedInteriorBranches = 0;
  readonly traffic: TrafficSurface;
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
  ) {
    this.traffic = new TrafficSurface(city);
    this.build();
    this.authoredPlaces = new Map(this.places);
  }
  /** Only used after the same authored city has been physically restored. */
  restore(): void {
    this.state.clear();
    this.invalidated.clear();
    this.availability = new WeakMap();
    this.places = new Map(this.authoredPlaces);
    this.corridors.clear();
    this.searchCount = this.searchVisits = this.searchLinkChecks = this.compressedSearchSteps = 0;
    this.prunedInteriorBranches = 0;
  }
  private node(p: Vec, layer = 'street', buildingId?: number, supportCell?: number): number {
    const id = this.nodes.length;
    this.nodes.push({ ...p, id, layer, buildingId, supportCell, restricted: militaryZone(this.city, p) });
    const bin = `${Math.floor(p.x / 16)},${Math.floor(p.z / 16)}`;
    let list = this.bins.get(bin);
    if (!list) this.bins.set(bin, (list = []));
    list.push(this.nodes[id]);
    return id;
  }
  private link(a: number, b: number, kind: WalkLink['kind'] = 'walk', extra: Partial<WalkLink> = {}): void {
    if (a === b) return;
    const link: WalkLink = { id: this.links.length, a, b, kind, ...extra };
    this.links.push(link);
    const A = this.nodes[a],
      B = this.nodes[b];
    link.distance = this.distance(A, B);
    for (let x = Math.floor(Math.min(A.x, B.x) / 16); x <= Math.floor(Math.max(A.x, B.x) / 16); x++)
      for (let z = Math.floor(Math.min(A.z, B.z) / 16); z <= Math.floor(Math.max(A.z, B.z) / 16); z++) {
        const key = `${x},${z}`;
        let set = this.linkBins.get(key);
        if (!set) this.linkBins.set(key, (set = new Set()));
        set.add(link.id);
      }
    for (const id of [a, b]) {
      let list = this.adjacent.get(id);
      if (!list) this.adjacent.set(id, (list = []));
      list.push(link);
    }
    this.indexLayerPortals();
  }
  private indexLayerPortals(): void {
    // Authored links are appended. Index each boundary once during construction;
    // the small incremental tail also supports subsequently added task links.
    while (this.portalLinks < this.links.length) {
      const link = this.links[this.portalLinks++],
        a = this.nodes[link.a],
        b = this.nodes[link.b];
      if (a.layer === b.layer) continue;
      for (const node of [a, b]) {
        if (!node.layer.startsWith('interior:')) continue;
        let portals = this.layerPortals.get(node.layer);
        if (!portals) this.layerPortals.set(node.layer, (portals = new Set()));
        // Two distinct portals are enough to prohibit this optimisation.
        if (portals.size < 2) portals.add(node.id);
      }
    }
  }
  private build(): void {
    const graph = this.city.roadGraph;
    if (!graph) return;
    const corners = new Map<number, number[]>(),
      extent = new Map<number, number>();
    for (const n of graph.nodes) {
      const half = Math.max(
        ...graph.edges.filter((e) => e.from === n.id || e.to === n.id).map((e) => e.width / 2 + 1),
      );
      extent.set(n.id, half);
      const ids = [
        [-1, -1],
        [1, -1],
        [1, 1],
        [-1, 1],
      ].map(([x, z]) => this.node({ x: n.x + x * half, y: (n.y ?? GROUND) + 0.12, z: n.z + z * half }));
      corners.set(n.id, ids);
      for (let i = 0; i < 4; i++)
        this.link(ids[i], ids[(i + 1) % 4], 'crossing', {
          junction: n.id,
          axis: i % 2,
          resource: `crossing:${n.id}:${i}`,
        });
    }
    for (const edge of graph.edges) {
      const a = graph.nodes.find((n) => n.id === edge.from)!,
        b = graph.nodes.find((n) => n.id === edge.to)!,
        directLength = Math.hypot(b.x - a.x, b.z - a.z),
        length = edge.curved ? roadLength(edge) : directLength,
        dx = (b.x - a.x) / directLength,
        dz = (b.z - a.z) / directLength;
      for (const side of [-1, 1]) {
        const lateral = (edge.width / 2 + 0.75) * side,
          start = extent.get(a.id)!,
          end = length - extent.get(b.id)!;
        let previous: number | undefined;
        const parts = Math.max(1, Math.ceil((end - start) / (edge.curved ? 0.9 : 3)));
        for (let i = 0; i <= parts; i++) {
          const along = start + ((end - start) * i) / parts;
          const point = edge.curved
            ? roadPoint(edge, along / length, lateral)
            : {
                x: a.x + dx * along - dz * lateral,
                y: 0,
                z: a.z + dz * along + dx * lateral,
              };
          const { x, z } = point;
          const id = this.node(
            {
              x,
              y: edge.curved
                ? roadSurfaceHeight(edge, x, z)
                : roadHeight(edge, x, z) + (edge.profile?.length ? 0.12 : 0),
              z,
            },
            edge.layer ?? 'street',
          );
          if (previous !== undefined) this.link(previous, id);
          if (i === 0 || i === parts) {
            const ids = corners.get(i === 0 ? a.id : b.id)!;
            const nearest = ids.reduce(
              (best, id2) =>
                this.distance(this.nodes[id], this.nodes[id2]) <
                this.distance(this.nodes[id], this.nodes[best])
                  ? id2
                  : best,
              ids[0],
            );
            this.link(id, nearest);
          }
          previous = id;
        }
      }
    }
    for (const path of this.city.coast?.paths ?? []) {
      const priorCount = this.nodes.length,
        authored: number[] = [];
      for (let segment = 1; segment < path.points.length; segment++) {
        const a = path.points[segment - 1],
          b = path.points[segment],
          steps = Math.max(1, Math.ceil(this.distance(a, b) / 2));
        for (let i = segment === 1 ? 0 : 1; i <= steps; i++) {
          const t = i / steps,
            point = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t },
            floor = this.physics.ground(point, 0.35, 0.45);
          if (floor) point.y = floor.y;
          const id = this.node(point);
          if (authored.length) this.link(authored.at(-1)!, id);
          authored.push(id);
        }
      }
      for (const id of [authored[0], authored.at(-1)]) {
        if (id === undefined) continue;
        const target = this.nodes[id],
          near = this.near(target, 4)
            .filter((n) => n.id < priorCount)
            .sort((a, b) => this.distance(a, target) - this.distance(b, target));
        const join = near.find((n) => this.traffic.clear(n, target) && this.lineOpen(n, target));
        if (join) this.link(id, join.id);
      }
    }
    const outdoorCount = this.nodes.length;
    const connectOutdoor = (id: number) => {
      const target = this.nodes[id];
      const near = this.near(target, 128)
        .filter((n) => n.id < outdoorCount && Math.abs(n.y - target.y) < 0.4)
        .map((n) => ({ n, d: this.distance(n, target) }))
        .sort((a, b) => a.d - b.d)
        .slice(0, 96);
      for (const { n } of near)
        if (this.traffic.clear(target, n) && this.lineOpen(target, n)) {
          this.link(id, n.id);
          return true;
        }
      return false;
    };
    for (const room of this.city.interiors ?? []) {
      const b = this.city.buildings.find((v) => v.id === room.buildingId)!,
        floors = new Map<number, Map<string, number>>();
      for (const floor of room.floors) {
        const nodes = new Map<string, number>();
        floors.set(floor, nodes);
        const y = (b.baseY ?? GROUND) + (floor * room.floorHeight + 1) * CELL,
          layer = `interior:${b.id}:${floor}`;
        for (let z = 1; z < b.d - 1; z++)
          for (let x = 1; x < b.w - 1; x++) {
            const floorCell = b.cells.find((c) => c.x === x && c.z === z && c.y === floor * room.floorHeight);
            const point = { x: b.x + (x + 0.5) * CELL, y, z: b.z + (z + 0.5) * CELL };
            if (!floorCell || this.physics.blocked(point.x, point.z, 0.23, y + 0.84)) continue;
            nodes.set(`${x},${z}`, this.node(point, layer, b.id, floorCell.id));
          }
        for (const [key, id] of nodes) {
          const [x, z] = key.split(',').map(Number);
          for (const next of [nodes.get(`${x + 1},${z}`), nodes.get(`${x},${z + 1}`)])
            if (next !== undefined) this.link(id, next);
        }
        for (const place of (this.city.places ?? []).filter(
          (p) => p.buildingId === b.id && p.floor === floor,
        )) {
          const nearest = [...nodes.values()].sort(
            (a, c) => this.distance(this.nodes[a], place) - this.distance(this.nodes[c], place),
          )[0];
          if (nearest !== undefined && this.distance(this.nodes[nearest], place) < 0.3)
            this.places.set(place.id, nearest);
        }
      }
      const inside = floors.get(0)?.get(`${room.doorX},1`);
      if (inside !== undefined) {
        const exit = floors.get(0)?.get(`${room.doorX + 1},1`);
        let landing: number | undefined;
        for (const lane of exit === undefined ? [0] : [0, 1]) {
          const x = room.entrance.x + lane * CELL,
            outside = this.node({ ...room.entrance, x }),
            resource = `door:${b.id}:${lane ? 'out' : 'in'}`;
          connectOutdoor(outside);
          // Both lanes share the supported landing. A fence or adjacent building
          // may allow only one lane a direct line to the public pavement.
          if (
            landing !== undefined &&
            this.traffic.clear(this.nodes[landing], this.nodes[outside]) &&
            this.lineOpen(this.nodes[landing], this.nodes[outside])
          )
            this.link(landing, outside);
          landing = outside;
          const path = [outside];
          for (let s = 0; s < 5; s++)
            path.push(
              this.node(
                { x, y: GROUND + (s + 1) * 0.25, z: b.z - 1.5 + (s + 0.5) * 0.3 },
                `entrance:${b.id}`,
                b.id,
              ),
            );
          path.push(
            this.node({ x, y: GROUND + CELL, z: b.z + 0.5 * CELL }, `interior:${b.id}:0`, b.id),
            lane ? exit! : inside,
          );
          if (lane) path.reverse();
          for (let i = 1; i < path.length; i++)
            this.link(path[i - 1], path[i], 'door', {
              resource,
              buildingId: b.id,
              oneWay: exit !== undefined,
            });
        }
      }
      for (let i = 0; i < room.floors.length - 1; i++) {
        const lower = floors.get(room.floors[i])!,
          upper = floors.get(room.floors[i + 1])!;
        const connected = (root: number): Set<number> => {
          const seen = new Set([root]),
            pending = [root],
            layer = this.nodes[root].layer;
          for (let n = 0; n < pending.length; n++)
            for (const link of this.adjacent.get(pending[n]) ?? []) {
              const next = link.a === pending[n] ? link.b : link.a;
              if (
                link.kind !== 'walk' ||
                seen.has(next) ||
                this.nodes[next].layer !== layer ||
                !this.open(link, 0)
              )
                continue;
              seen.add(next);
              pending.push(next);
            }
          return seen;
        };
        const lowerRoot = lower.get(`${room.doorX},1`) ?? lower.values().next().value,
          represented = (this.city.places ?? []).find(
            (p) => p.buildingId === b.id && p.floor === room.floors[i + 1] && this.places.has(p.id),
          ),
          upperRoot = represented ? this.places.get(represented.id) : upper.values().next().value,
          lowerConnected = lowerRoot === undefined ? new Set<number>() : connected(lowerRoot),
          upperConnected = upperRoot === undefined ? new Set<number>() : connected(upperRoot);
        const common = [...lower.keys()].filter(
          (key) =>
            upper.has(key) && lowerConnected.has(lower.get(key)!) && upperConnected.has(upper.get(key)!),
        );
        common.sort((a, b) => {
          const [ax, az] = a.split(',').map(Number),
            [bx, bz] = b.split(',').map(Number);
          return Math.hypot(ax - room.liftX, az - room.liftZ) - Math.hypot(bx - room.liftX, bz - room.liftZ);
        });
        const selected = common[0];
        if (selected) [room.liftX, room.liftZ] = selected.split(',').map(Number);
        const a = selected ? lower.get(selected) : undefined,
          b2 = selected ? upper.get(selected) : undefined;
        if (a !== undefined && b2 !== undefined)
          this.link(a, b2, 'lift', { resource: `lift:${b.id}`, buildingId: b.id });
      }
      if (b.id === this.city.airport?.terminal) {
        const airport = this.city.airport,
          inside = floors.get(0)?.get('28,1');
        if (inside !== undefined) {
          let previous = inside;
          const door = this.node({ x: 407.0, y: GROUND + CELL, z: -388.125 }, `interior:${b.id}:0`, b.id);
          this.link(previous, door, 'door', { resource: 'airport-airside', buildingId: b.id });
          previous = door;
          for (let s = 0; s < 5; s++) {
            const next = this.node(
              { x: 407.65 + s * 0.3, y: GROUND + 1.25 - s * 0.25, z: -388.125 },
              'airport',
              b.id,
            );
            this.link(previous, next, 'door', { resource: 'airport-airside', buildingId: b.id });
            previous = next;
          }
          const walkTo = (point: Vec) => {
            const start = this.nodes[previous],
              parts = Math.max(1, Math.ceil(this.distance(start, point) / 2));
            for (let i = 1; i <= parts; i++) {
              const next = this.node(
                {
                  x: start.x + ((point.x - start.x) * i) / parts,
                  y: start.y + ((point.y - start.y) * i) / parts,
                  z: start.z + ((point.z - start.z) * i) / parts,
                },
                'airport',
              );
              this.link(previous, next);
              previous = next;
            }
          };
          for (const point of airport.walkway) walkTo(point);
          for (const stand of airport.stands) {
            previous = this.nearest({ x: stand.x - 4.5, y: stand.y, z: -379 }, 'airport');
            walkTo({ x: stand.x - 4.5, y: stand.y, z: stand.z - 5 });
            let next = this.node({ x: stand.x - 3.7, y: stand.y, z: stand.z - 5 }, 'airport');
            this.link(previous, next);
            previous = next;
            for (let s = 0; s < 6; s++) {
              next = this.node(
                {
                  x: stand.x - 3.35 + s * 0.3,
                  y: GROUND + ((stand.y - GROUND + 0.035 + 1.475) * (s + 1)) / 6,
                  z: stand.z - 5,
                },
                'airport',
              );
              this.link(previous, next, 'door', { resource: `aircraft-stair:${stand.x}` });
              previous = next;
            }
          }
        }
      }
    }
    for (const facility of this.city.facilities ?? []) {
      const b = this.city.buildings.find((b) => b.id === facility.buildingId)!,
        grid = new Map<string, number>();
      for (let x = 1; x < b.w - 1; x++)
        for (let z = 1; z < b.d - 1; z++) {
          const point = { x: b.x + (x + 0.5) * CELL, y: GROUND + 0.2, z: b.z + (z + 0.5) * CELL };
          if (this.physics.blocked(point.x, point.z, 0.23, point.y + 0.85)) continue;
          const cell = b.cells.find((c) => c.x === x && c.z === z && c.y === 0);
          if (cell) grid.set(`${x},${z}`, this.node(point, `facility:${b.id}`, b.id, cell.id));
        }
      for (const [position, id] of grid) {
        const [x, z] = position.split(',').map(Number);
        for (const next of [grid.get(`${x + 1},${z}`), grid.get(`${x},${z + 1}`)])
          if (next !== undefined) this.link(id, next);
      }
      for (const place of facility.duties ?? []) {
        const nearest = [...grid.values()].sort(
          (a, b) => this.distance(this.nodes[a], place) - this.distance(this.nodes[b], place),
        )[0];
        if (nearest !== undefined && this.distance(this.nodes[nearest], place) < 0.4) {
          const id = this.node(place, `facility:${b.id}`, b.id, place.supportCell);
          this.link(nearest, id);
          this.places.set(place.id, id);
        }
      }
      const edge = graph.edges.find((e) => e.id === facility.edge)!,
        a = graph.nodes.find((n) => n.id === edge.from)!,
        z = a.z + edge.width / 2 + 0.75;
      const outside = this.node({ x: facility.entry.x, y: roadHeight(edge, facility.entry.x, z) + 0.12, z });
      connectOutdoor(outside);
      // A real prop can occupy one sampled point in a wide garage. Connect a
      // physically clear entrance across its actual opening instead of making
      // the whole station inaccessible when only the centre sample is blocked.
      const inside = [2, 3, 4, 5, 6, 7]
        .map((x) => grid.get(`${x},1`))
        .filter((id): id is number => id !== undefined)
        .sort(
          (a, b) =>
            this.distance(this.nodes[a], this.nodes[outside]) -
            this.distance(this.nodes[b], this.nodes[outside]),
        )
        .find((id) => this.lineOpen(this.nodes[outside], this.nodes[id], true));
      if (inside !== undefined)
        this.link(outside, inside, 'door', { resource: `door:${b.id}`, buildingId: b.id });
    }
    for (const station of railStations(this.city.rail)) {
      const b = this.city.buildings.find((b) => b.id === station.platform)!,
        nodes = new Map<string, number>(),
        layer = `platform:${station.id}`;
      for (let x = 1; x < b.w - 1; x++)
        for (let z = 0; z < b.d; z++) {
          const p = {
            x: b.x + station.direction.x * (x + 0.5) * CELL + station.side.x * (z + 0.5) * CELL,
            y: station.lift.y,
            z: b.z + station.direction.z * (x + 0.5) * CELL + station.side.z * (z + 0.5) * CELL,
          };
          if (this.physics.blocked(p.x, p.z, 0.24, p.y + 0.85)) continue;
          nodes.set(`${x},${z}`, this.node(p, layer, b.id, b.id * 1e6 + x + 32 * z + 1024 * 6));
        }
      for (const [key, id] of nodes) {
        const [x, z] = key.split(',').map(Number);
        for (const next of [nodes.get(`${x + 1},${z}`), nodes.get(`${x},${z + 1}`)])
          if (next !== undefined) this.link(id, next);
      }
      const support = this.physics.ground(station.entrance, 0.35, 0.5);
      if (support) station.entrance.y = support.y;
      const ground = this.node(station.entrance),
        lift = [...nodes.values()].find((id) => this.distance(this.nodes[id], station.lift) < 0.05);
      if (connectOutdoor(ground) && lift !== undefined)
        this.link(ground, lift, 'lift', { resource: `station-lift:${station.id}`, buildingId: b.id });
      for (const place of station.queues) {
        const closest = [...nodes.values()].find((id) => this.distance(this.nodes[id], place) < 0.1);
        if (closest !== undefined) this.places.set(place.id, closest);
      }
    }
    for (const place of this.city.places ?? [])
      if (
        !this.places.has(place.id) &&
        !place.id.startsWith('rail-queue:') &&
        !this.city.interiors?.some((r) => r.buildingId === place.buildingId)
      ) {
        place.y = this.physics.ground(place, 0.36, 0.5)?.y ?? place.y;
        const id = this.node(place);
        if (connectOutdoor(id)) this.places.set(place.id, id);
      }
  }
  private distance(a: Vec, b: Vec): number {
    const x = a.x - b.x,
      y = a.y - b.y,
      z = a.z - b.z;
    return Math.sqrt(x * x + y * y + z * z);
  }
  lineOpen(a: Vec, b: Vec, ignoreSmallSteps = false): boolean {
    const count = Math.max(1, Math.ceil(this.distance(a, b) / 0.65));
    for (let i = 0; i <= count; i++) {
      const t = i / count,
        p = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t };
      if (this.physics.blocked(p.x, p.z, 0.23, p.y + 1.03)) return false;
      const support = this.physics.ground(p, 0.35, 0.45);
      if (!ignoreSmallSteps && !support) return false;
      if (support?.owner === -1 && inRiver(this.city, p, 0.3)) return false;
    }
    return true;
  }
  available(place: Place): boolean {
    if (!this.places.has(place.id)) return false;
    const prior = this.availability.get(place);
    if (
      prior?.version === this.physics.structureVersion &&
      prior.support === place.supportCell &&
      prior.furniture === place.furnitureId
    )
      return prior.value;
    const supportVersion = this.physics.buildingSupportVersion(
        place.supportCell === undefined ? undefined : Math.floor(place.supportCell / 1e6),
      ),
      furnitureVersion = this.physics.buildingSupportVersion(place.furnitureId);
    if (
      prior &&
      prior.support === place.supportCell &&
      prior.furniture === place.furnitureId &&
      prior.supportVersion === supportVersion &&
      prior.furnitureVersion === furnitureVersion
    ) {
      prior.version = this.physics.structureVersion;
      return prior.value;
    }
    let value = true;
    for (const cellId of [
      place.supportCell,
      place.furnitureId === undefined ? undefined : place.furnitureId * 1000000,
    ]) {
      if (cellId === undefined) continue;
      const owner = this.physics.cellOwner.get(cellId),
        c =
          owner === undefined
            ? undefined
            : (this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner));
      if (!c?.anchored) {
        value = false;
        break;
      }
    }
    this.availability.set(place, {
      version: this.physics.structureVersion,
      support: place.supportCell,
      furniture: place.furnitureId,
      supportVersion,
      furnitureVersion,
      value,
    });
    return value;
  }
  open(link: WalkLink, now: number): boolean {
    const prior = this.state.get(link.id);
    if (prior) return prior.open;
    const a = this.nodes[link.a],
      b = this.nodes[link.b];
    let open = true;
    for (const node of [a, b])
      if (node.supportCell !== undefined) {
        const owner = this.physics.cellOwner.get(node.supportCell),
          c =
            owner === undefined
              ? undefined
              : (this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner));
        if (!c?.anchored) open = false;
      }
    if (open && link.kind !== 'lift') {
      open = this.lineOpen(a, b, link.kind === 'door');
      // Platform furniture can sit below the old chest-height point samples.
      // Validate the same complete walking body used by the real passenger.
      if (open && a.layer.startsWith('platform:') && a.layer === b.layer)
        open = this.physics.sweepFoot(a, { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z }) >= 0.999;
    }
    this.state.set(link.id, { at: now, open });
    this.cacheWrites++;
    return open;
  }
  invalidate(buildingId?: number): void {
    if (buildingId === undefined) {
      this.state.clear();
      this.invalidated.clear();
      return;
    }
    const building = this.physics.buildings.find((b) => b.id === buildingId);
    if (!building) return;
    const radius = Math.hypot(building.w, building.d) * CELL + 5,
      ids = new Set<number>();
    const prior = this.invalidated.get(buildingId);
    // No newly cached path can need deleting twice in one uninterrupted
    // fracture batch. A path read/write or moved building invalidates this proof.
    if (
      prior?.writes === this.cacheWrites &&
      prior.x === building.x &&
      prior.z === building.z &&
      prior.radius === radius
    )
      return;
    this.invalidated.set(buildingId, { writes: this.cacheWrites, x: building.x, z: building.z, radius });
    for (let x = Math.floor((building.x - radius) / 16); x <= Math.floor((building.x + radius) / 16); x++)
      for (let z = Math.floor((building.z - radius) / 16); z <= Math.floor((building.z + radius) / 16); z++)
        for (const id of this.linkBins.get(`${x},${z}`) ?? []) ids.add(id);
    for (const id of ids) {
      const link = this.links[id];
      const a = this.nodes[link.a],
        b = this.nodes[link.b];
      if (
        a.buildingId === buildingId ||
        b.buildingId === buildingId ||
        (Math.min(a.x, b.x) < building.x + radius &&
          Math.max(a.x, b.x) > building.x - radius &&
          Math.min(a.z, b.z) < building.z + radius &&
          Math.max(a.z, b.z) > building.z - radius)
      )
        this.state.delete(link.id);
    }
  }
  invalidateAt(point: Vec, radius = 4): void {
    for (let x = Math.floor((point.x - radius) / 16); x <= Math.floor((point.x + radius) / 16); x++)
      for (let z = Math.floor((point.z - radius) / 16); z <= Math.floor((point.z + radius) / 16); z++)
        for (const id of this.linkBins.get(`${x},${z}`) ?? []) {
          const link = this.links[id],
            a = this.nodes[link.a],
            b = this.nodes[link.b];
          if (Math.max(a.y, b.y) < point.y - 1 || Math.min(a.y, b.y) > point.y + 2) continue;
          this.state.delete(id);
        }
  }
  nearest(p: Vec, layer?: string): number {
    let best = -1,
      distance = Infinity;
    for (const radius of [4, 12, 32, 64]) {
      for (const node of this.near(p, radius, layer)) {
        if (layer && node.layer !== layer) continue;
        const d = this.distance(p, node);
        if (d < distance && Math.abs(node.y - p.y) < 0.6) {
          best = node.id;
          distance = d;
        }
      }
      if (best >= 0 && distance <= radius) return best;
    }
    return best;
  }
  near(p: Vec, radius: number, layer?: string): WalkNode[] {
    const nodes: WalkNode[] = [];
    for (let x = Math.floor((p.x - radius) / 16); x <= Math.floor((p.x + radius) / 16); x++)
      for (let z = Math.floor((p.z - radius) / 16); z <= Math.floor((p.z + radius) / 16); z++)
        for (const n of this.bins.get(`${x},${z}`) ?? [])
          if ((!layer || n.layer === layer) && Math.hypot(n.x - p.x, n.z - p.z) <= radius) nodes.push(n);
    return nodes;
  }
  route(from: number, to: number, now: number): number[] {
    const search = this.search(from, to, now);
    let result = search.next();
    while (!result.done) result = search.next();
    return result.value;
  }
  /** Degree-two authored waypoints are not route decisions. Retain every one
   * in the result and check every live link, but avoid a heap/hash record at
   * each metre of the same pavement. Short bounded chains keep generator
   * slices small; the cache stores topology only, never live accessibility. */
  private searchCorridors(from: number): WalkCorridor[] {
    if (this.corridorNodeCount !== this.nodes.length || this.corridorLinkCount !== this.links.length) {
      this.corridors.clear();
      this.corridorNodeCount = this.nodes.length;
      this.corridorLinkCount = this.links.length;
    }
    const existing = this.corridors.get(from);
    if (existing) return existing;
    const result: WalkCorridor[] = [];
    for (const first of this.adjacent.get(from) ?? []) {
      const nodes: number[] = [],
        links: WalkLink[] = [];
      let current = from,
        link = first;
      while (links.length < 32) {
        const next = link.a === current ? link.b : link.a;
        links.push(link);
        nodes.push(next);
        const outgoing = this.adjacent.get(next);
        if (next === from || outgoing?.length !== 2) break;
        current = next;
        link = outgoing[0] === link ? outgoing[1] : outgoing[0];
      }
      result.push({ nodes, links });
    }
    if (this.corridors.size >= 8192) this.corridors.delete(this.corridors.keys().next().value!);
    this.corridors.set(from, result);
    return result;
  }
  *search(
    from: number,
    to: number,
    now: number,
    blocked: ReadonlySet<number> = new Set(),
    hardBlocked = false,
    restrictedAccess = false,
  ): Generator<void, number[]> {
    this.searchCount++;
    if (!this.nodes[from] || !this.nodes[to]) return [];
    if (from === to) return [from];
    this.indexLayerPortals();
    const endpointLayers = new Set([this.nodes[from].layer, this.nodes[to].layer]);
    const allowRestricted = restrictedAccess || !!this.nodes[from].restricted;
    if (!allowRestricted && this.nodes[to].restricted) return [];
    // Buildings are leaves of the street network. Enter only the origin and
    // destination buildings; thousands of unrelated desks are not through roads.
    const buildings = new Set([undefined, this.nodes[from].buildingId, this.nodes[to].buildingId]);
    // The terminal is a real through-building between airside stairs and the street.
    if (this.city.airport && [this.nodes[from], this.nodes[to]].some((n) => n.layer === 'airport'))
      buildings.add(this.city.airport.terminal);
    // Keep each visited node's search state in one sparse record. Hundreds of
    // concurrent post-damage routes must not allocate full-city dense arrays,
    // or repeatedly hash the same node through three independent containers.
    interface Visit {
      cost: number;
      previous: number;
      closed: boolean;
      heuristic: number;
      via: number[];
    }
    const ends = [from, to],
      pending = [new MinQueue<number>(), new MinQueue<number>()],
      records = [new Map<number, Visit>(), new Map<number, Visit>()],
      initial = this.distance(this.nodes[from], this.nodes[to]);
    records[0].set(from, { cost: 0, previous: from, closed: false, heuristic: initial, via: [] });
    records[1].set(to, { cost: 0, previous: to, closed: false, heuristic: initial, via: [] });
    pending[0].push(from, initial);
    pending[1].push(to, initial);
    let visits = 0,
      linkWork = 0,
      physicalWork = 0;
    let meeting: number | undefined;
    // Search both ends so an isolated destination is rejected after exploring
    // its component, rather than flooding the entire accessible regional city.
    while (pending[0].size && pending[1].size && visits++ <= this.nodes.length * 8) {
      if (visits % 16 === 0) yield;
      const side = visits % 2,
        other = 1 - side,
        current = pending[side].pop()!.value,
        record = records[side].get(current)!;
      if (record.closed) continue;
      this.searchVisits++;
      record.closed = true;
      if (records[other].get(current)?.closed) {
        meeting = current;
        break;
      }
      for (const corridor of this.searchCorridors(current)) {
        let end = corridor.nodes.length - 1;
        const originIndex = corridor.nodes.indexOf(from),
          targetIndex = corridor.nodes.indexOf(to);
        if (originIndex >= 0) end = Math.min(end, originIndex);
        if (targetIndex >= 0) end = Math.min(end, targetIndex);
        // As in an ordinary graph search, do not retest the edge back into
        // an already closed record. A compressed corridor may contain 32 links.
        if (records[side].get(corridor.nodes[end])?.closed) continue;
        let next = current,
          cost = record.cost,
          length = 0,
          accessible = true;
        for (let index = 0; index <= end; index++) {
          const link = corridor.links[index],
            previous = next;
          // Cold links can invoke physical queries and keep the small quantum.
          // Cached map lookups use the original scale of graph work per yield;
          // yielding after every few cheap links starved hundreds of real trips.
          if (++linkWork % 64 === 0 || (!this.state.has(link.id) && ++physicalWork % 16 === 0)) yield;
          next = corridor.nodes[index];
          this.searchLinkChecks++;
          const nextNode = this.nodes[next],
            portals = this.layerPortals.get(nextNode.layer);
          if (portals?.size === 1 && !endpointLayers.has(nextNode.layer) && !portals.has(next)) {
            // An unrelated room reached through exactly one portal can only
            // return to that same node. Exploring its desks cannot produce a
            // useful through route. Keep the portal itself for continuing up
            // or down the lift, and retain complete search on endpoint floors
            // and every floor with distinct arrival/departure portals.
            this.prunedInteriorBranches++;
            accessible = false;
            break;
          }
          if (
            (link.oneWay && (side === 0 ? previous !== link.a : previous !== link.b)) ||
            (!allowRestricted && this.nodes[next].restricted) ||
            !buildings.has(this.nodes[next].buildingId)
          ) {
            accessible = false;
            break;
          }
          // A waiting person occupies part of a pavement, not the whole street
          // node. Prefer a less crowded route, while local body clearance still
          // controls passing. Interior door/furniture obstructions remain hard.
          if (
            (blocked.has(next) &&
              next !== to &&
              next !== from &&
              (hardBlocked || this.nodes[next].layer !== 'street')) ||
            !this.open(link, now)
          ) {
            accessible = false;
            break;
          }
          cost +=
            (link.distance ?? this.distance(this.nodes[previous], this.nodes[next])) +
            (link.kind === 'crossing' ? 2 : link.kind === 'lift' ? 8 : 0) +
            (blocked.has(next) && next !== to && next !== from ? 6 : 0);
          length++;
          // Either requested endpoint may be halfway along a cached chain.
          if (next === from || next === to) break;
        }
        if (!accessible || next === current) continue;
        const known = records[side].get(next);
        if (known?.closed) continue;
        if (cost < (known?.cost ?? Infinity)) {
          const visit = known ?? {
            cost,
            previous: current,
            closed: false,
            heuristic: this.distance(this.nodes[next], this.nodes[ends[other]]),
            via: [],
          };
          visit.cost = cost;
          visit.previous = current;
          visit.via = corridor.nodes.slice(0, length);
          this.compressedSearchSteps += length - 1;
          if (!known) records[side].set(next, visit);
          pending[side].push(next, cost + visit.heuristic);
        }
      }
    }
    if (meeting === undefined) return [];
    const result: number[] = [];
    let current = meeting;
    while (current !== from && result.length <= this.nodes.length) {
      const visit = records[0].get(current)!;
      for (let i = visit.via.length - 1; i >= 0; i--) result.push(visit.via[i]);
      current = visit.previous;
    }
    result.push(from);
    result.reverse();
    current = meeting;
    while (current !== to && result.length <= this.nodes.length) {
      const visit = records[1].get(current)!;
      for (let i = visit.via.length - 2; i >= 0; i--) result.push(visit.via[i]);
      current = visit.previous;
      result.push(current);
    }
    return result;
  }
  edge(a: number, b: number): WalkLink | undefined {
    return this.adjacent.get(a)?.find((l) => (l.a === a ? l.b : l.a) === b);
  }
}
